import { useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Filter, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const CONTROL = "h-9 rounded-[8px] border border-[#E5E0DA] bg-white px-3 text-[12.5px] text-[#2D2D2D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-accent,#C75A1B)]";

function PeopleFilter({ browser, prefix }) {
    const [search, setSearch] = useState("");
    const { filters, change, options, optionsLoading, optionsError, retry } = browser;
    const matching = options.people.filter((person) => person.name.toLowerCase().includes(search.trim().toLowerCase()));
    function toggle(name) {
        change({ people: filters.people.includes(name) ? filters.people.filter((item) => item !== name) : [...filters.people, name] });
    }
    return (
        <Popover onOpenChange={() => setSearch("")}>
            <PopoverTrigger asChild>
                <button type="button" data-testid={`${prefix}-person-filter`} aria-label="Filter by person"
                    className={`${CONTROL} inline-flex items-center justify-between gap-2 min-w-[180px] max-w-full`}
                    style={filters.people.length ? { borderColor: "var(--brand-accent,#C75A1B)" } : undefined}>
                    <Filter className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{filters.people.length === 0 ? "All people" : filters.people.length === 1 ? filters.people[0] : `${filters.people.length} people selected`}</span>
                    <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 max-w-[calc(100vw-2rem)] p-0 bg-white" aria-label="Choose people">
                <div className="p-3 border-b border-[#E5E0DA]">
                    <label className="text-[12px] font-semibold text-[#2D2D2D]" htmlFor={`${prefix}-person-search`}>Filter by person</label>
                    <input id={`${prefix}-person-search`} data-testid={`${prefix}-person-search`} value={search}
                        onChange={(event) => setSearch(event.target.value)} placeholder="Search people…"
                        className={`${CONTROL} w-full mt-2`} />
                    <button type="button" className="mt-2 text-[12px] font-semibold text-[#6F6A64] underline"
                        onClick={() => change({ people: [] })}>Clear person filter (show all)</button>
                </div>
                <div className="max-h-64 overflow-y-auto p-2">
                    {optionsLoading ? <p role="status" className="p-2 text-[12px] text-[#6F6A64]">Loading people…</p>
                        : optionsError ? <p role="alert" className="p-2 text-[12px]">Could not load people. <button type="button" className="underline" onClick={retry}>Retry</button></p>
                        : !matching.length ? <p className="p-2 text-[12px] text-[#6F6A64]">No matching people.</p>
                        : matching.map((person) => (
                            <label key={person.name} className="flex items-center gap-2 rounded-md px-2 py-2 text-[12.5px] cursor-pointer hover:bg-[#F6F4F0]">
                                <input type="checkbox" checked={filters.people.includes(person.name)} onChange={() => toggle(person.name)}
                                    className="shrink-0 accent-[var(--brand-accent,#C75A1B)]" />
                                <span className="flex-1 break-words">{person.name}</span>
                                <span className="text-[11px] text-[#9A968F]">{person.count.toLocaleString()}</span>
                            </label>
                        ))}
                </div>
                <p className="border-t border-[#E5E0DA] p-3 text-[11px] text-[#6F6A64]">Tick one or more people. No selection shows everyone.</p>
            </PopoverContent>
        </Popover>
    );
}

export function AuditLogFilters({ browser, prefix, placeholder, children }) {
    const { filters, change, clear, error, retry } = browser;
    const active = !!(filters.q || filters.people.length || filters.date_from || filters.date_to || filters.action || filters.category !== "all");
    return (
        <div className="space-y-3">
            <div className="flex items-end gap-3 flex-wrap">
                <label className="flex-1 min-w-[200px] max-w-[340px]">
                    <span className="block mb-1 text-[11px] font-semibold text-[#6F6A64]">Search</span>
                    <span className={`${CONTROL} flex items-center gap-2`}>
                        <Search className="w-3.5 h-3.5 text-[#6F6A64] shrink-0" />
                        <input data-testid={`${prefix}-search`} aria-label="Search audit events" value={filters.q} maxLength={300}
                            onChange={(event) => change({ q: event.target.value })} placeholder={placeholder}
                            className="w-full min-w-0 bg-transparent outline-none text-[12.5px]" />
                    </span>
                </label>
                <div><span className="block mb-1 text-[11px] font-semibold text-[#6F6A64]">Person</span><PeopleFilter browser={browser} prefix={prefix} /></div>
                <label className="text-[11px] font-semibold text-[#6F6A64]">From
                    <input data-testid={`${prefix}-date-from`} type="date" value={filters.date_from} max={filters.date_to || undefined}
                        onChange={(event) => change({ date_from: event.target.value })} className={`${CONTROL} block mt-1 font-normal`} />
                </label>
                <label className="text-[11px] font-semibold text-[#6F6A64]">To
                    <input data-testid={`${prefix}-date-to`} type="date" value={filters.date_to} min={filters.date_from || undefined}
                        onChange={(event) => change({ date_to: event.target.value })} className={`${CONTROL} block mt-1 font-normal`} />
                </label>
                {children}
                {active && <button type="button" data-testid={`${prefix}-clear-filters`} onClick={clear}
                    className={`${CONTROL} font-semibold hover:bg-[#F6F4F0]`}>Clear filters</button>}
            </div>
            <p className="text-[11px] text-[#9A968F]">Dates include the full day in UK time. Filters search the full audit history.</p>
            {error && <div role="alert" className="rounded-lg border border-[#E7BCB4] bg-[#FFF4F1] p-3 text-[12.5px] text-[#8D3025]">
                {error} <button type="button" className="underline font-semibold" onClick={retry}>Retry</button>
            </div>}
        </div>
    );
}

export function AuditLogPagination({ browser, prefix }) {
    const { filters, change, total, loading, error } = browser;
    const pages = Math.max(1, Math.ceil(total / filters.page_size));
    const disabled = loading || !!error;
    const first = total ? (filters.page - 1) * filters.page_size + 1 : 0;
    const last = Math.min(filters.page * filters.page_size, total);
    const buttons = [
        ["First page", ChevronsLeft, 1, filters.page <= 1],
        ["Previous page", ChevronLeft, filters.page - 1, filters.page <= 1],
        ["Next page", ChevronRight, filters.page + 1, filters.page >= pages],
        ["Last page", ChevronsRight, pages, filters.page >= pages],
    ];
    return (
        <nav aria-label="Audit log pagination" data-testid={`${prefix}-pagination`} className="flex flex-wrap items-center justify-between gap-3 pb-12 text-[12px] text-[#6F6A64]">
            <span role="status" aria-live="polite">{loading ? "Loading events…" : error ? "Events unavailable" : `Showing ${first.toLocaleString()}–${last.toLocaleString()} of ${total.toLocaleString()} events`}</span>
            <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2">Rows per page
                    <select data-testid={`${prefix}-page-size`} value={filters.page_size} onChange={(event) => change({ page_size: Number(event.target.value) })}
                        className={`${CONTROL} px-2`}>
                        {[25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
                    </select>
                </label>
                <span>Page {filters.page} of {pages.toLocaleString()}</span>
                <div className="flex items-center gap-1">
                    {buttons.map(([label, Icon, page, boundary]) => <button key={label} type="button" aria-label={label} title={label}
                        disabled={disabled || boundary} onClick={() => change({ page })}
                        className={`${CONTROL} px-2 disabled:opacity-40 disabled:cursor-not-allowed hover:enabled:bg-[#F6F4F0]`}><Icon className="w-4 h-4" /></button>)}
                </div>
            </div>
        </nav>
    );
}
